/*
 * castle-rat-transition.js — "lights out" page transitions.
 *
 * Clicking an internal link fades the page to black (html.cr-lights-out →
 * css animation), then navigates while everything is dark. The destination
 * page arrives already black: a pre-paint inline script in theme.liquid
 * reads the sessionStorage flag set here and adds html.cr-lights-in before
 * first paint, and castle-rat.css plays the fade-up reveal — pure CSS, so
 * arrival never depends on this file having loaded. This script only
 * tidies up afterwards (removes the class so the overlay's layer is freed).
 *
 * Deliberately NOT intercepted: modified-key/middle clicks, targeted links,
 * downloads, cross-origin URLs, checkout, same-page anchors. Skipped in the
 * theme editor and (no delay, no animation) under prefers-reduced-motion.
 * bfcache restores strip the classes so a page never resumes mid-blackout.
 */
(function () {
  if (window.Shopify && window.Shopify.designMode) return;

  var html = document.documentElement;
  var FLAG = 'cr_lights';
  // A touch over the 260ms lights-out animation: a beat of full black before
  // the request goes out.
  var OUT_MS = 280;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // --- arrival ----------------------------------------------------------------
  // The page arrived black (.cr-lights-in from the pre-paint script). Hold the
  // dark until the first screen's assets are actually renderable — revealing at
  // first paint on a slow connection exposes images popping in piecemeal, which
  // reads cheap. "Ready" = every in-viewport <img>, the CSS background art
  // (backdrop fill, sign tablets), and the fonts are decoded — capped at
  // READY_CAP_MS so darkness never overstays. If this script never runs, the
  // CSS failsafe on .cr-lights-in fades the page up by itself at 2.5s.
  var READY_CAP_MS = 1800;

  function clearArrival() {
    html.classList.remove('cr-lights-in', 'cr-lights-reveal');
  }

  if (html.classList.contains('cr-lights-in')) {
    var reveal = function () {
      if (!html.classList.contains('cr-lights-in')) return;
      html.classList.remove('cr-lights-in');
      html.classList.add('cr-lights-reveal');
    };

    if (reduceMotion.matches) {
      // Overlay is already hidden by the reduced-motion CSS; just tidy up.
      clearArrival();
    } else {
      var inView = function (el) {
        var r = el.getBoundingClientRect();
        return r.bottom > 0 && r.top < window.innerHeight && r.width > 0 && r.height > 0;
      };
      var waits = [];
      if (document.fonts && document.fonts.ready) waits.push(document.fonts.ready);
      Array.prototype.forEach.call(document.images, function (img) {
        if (inView(img)) waits.push(img.decode().catch(function () {}));
      });
      // Art painted as CSS backgrounds is invisible to document.images — probe
      // the known carriers (backdrop fill, sign tablets, inline-styled art).
      Array.prototype.forEach.call(
        document.querySelectorAll('.cr-fill, .cr-overlay, .cr-tablet, [style*="background-image"]'),
        function (el) {
          if (!inView(el)) return;
          var match = getComputedStyle(el).backgroundImage.match(/url\(["']?([^"')]+)/);
          if (!match) return;
          var probe = new Image();
          probe.src = match[1];
          waits.push(probe.decode().catch(function () {}));
        }
      );
      Promise.race([
        Promise.all(waits),
        new Promise(function (resolve) {
          setTimeout(resolve, READY_CAP_MS);
        }),
      ]).then(reveal, reveal);
    }

    html.addEventListener('animationend', function (event) {
      if (event.animationName === 'cr-lights-in' || event.animationName === 'cr-lights-reveal') {
        clearArrival();
      }
    });
    setTimeout(clearArrival, 4000); // ultimate failsafe (animations disabled etc.)
  }

  // Never resume a bfcache-restored page mid-blackout (or with the rail
  // still tucked away from the departure).
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    clearArrival();
    html.classList.remove('cr-lights-out');
    var rail = document.querySelector('.cr-nav.is-tucking');
    if (rail) rail.classList.remove('is-tucking');
  });

  // --- departure ------------------------------------------------------------
  function shouldIntercept(link, event) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return false;
    var url;
    try {
      url = new URL(link.href, location.href);
    } catch (err) {
      return false;
    }
    if (url.origin !== location.origin) return false;
    // Checkout runs Shopify's own layout — hand it off untouched.
    if (/^\/(checkout|cart\/c\b)/.test(url.pathname)) return false;
    // Same-page anchor with a hash: let the browser just jump.
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return false;
    return true;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || !shouldIntercept(link, event)) return;

    event.preventDefault();
    var destination = link.href;
    try {
      sessionStorage.setItem(FLAG, '1');
    } catch (err) {
      /* private mode etc. — arrive bright, still seamless on black pages */
    }

    if (reduceMotion.matches) {
      location.href = destination;
      return;
    }

    html.classList.remove('cr-lights-in');
    html.classList.add('cr-lights-out');

    // The blackout sits UNDER the site chrome, so the sign rail stays
    // visible through the transition. If it's showing open when the
    // navigation starts — pinned/focused collapsible, or the always-open
    // home variant — tuck it now via .is-tucking (slides it to the next
    // page's collapsed peek), otherwise the rail looks like it teleports
    // sideways across the swap. Hold the navigation until the slide
    // (merchant-tunable duration) is done. Below 750px the rail is the
    // mobile drawer and manages itself.
    var delay = OUT_MS;
    if (window.matchMedia('(min-width: 750px)').matches) {
      var rail = document.querySelector('.cr-nav');
      var railShowing =
        rail &&
        (!rail.classList.contains('cr-nav--collapsible') ||
          rail.classList.contains('is-pinned') ||
          rail.matches(':focus-within'));
      if (railShowing) {
        rail.classList.remove('is-pinned');
        // The home rail's finished intro animation (fill: both) pins the
        // rail's transform forever, which would override the tuck — its job
        // is long done by departure time, so release it. Its end state
        // equals the base state, so nothing snaps.
        rail.classList.remove('cr-nav--intro');
        if (rail.contains(document.activeElement)) document.activeElement.blur();
        // Commit the post-animation style before adding the tuck: transitions
        // don't start from an animation-influenced value in the same recalc,
        // so without this flush the rail SNAPS to the tucked position.
        void rail.offsetWidth;
        rail.classList.add('is-tucking');
        var slideSeconds = parseFloat(getComputedStyle(rail).transitionDuration) || 0.32;
        delay = Math.max(OUT_MS, Math.round(slideSeconds * 1000) + 60);
      }
    }

    setTimeout(function () {
      location.href = destination;
    }, delay);
  });
})();
